/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User as UserIcon,
  Lock,
  Phone,
  Bell,
  Shield,
  Trash2,
  KeyRound,
  PartyPopper,
  MapPin,
  PhoneCall,
} from "lucide-react";
import { apiFetch } from "../auth/apiClient";
import { useAuth } from "../auth/AuthContext";
import { getCommunitySettings, updateCommunitySettings } from "./communityApi";
import "./SettingsPage.css";

// Notification categories shown on web settings. Curated to the ones most
// relevant for residents; the full list comes from the backend GET response.
// Keep this in sync with the mobile PrefsScreen (notification-prefs.ts).
const WEB_NOTIF_CATEGORIES = new Set([
  "announcement.published",
  "document.added",
  "document.comment_added",
  "villa_membership.submitted",
  "villa_membership.approved",
  "villa_membership.rejected",
  "phone_change.submitted",
  "phone_change.approved",
  "phone_change.rejected",
  "volunteer_team.applied",
  "volunteer_team.approved",
  "volunteer_team.rejected",
  "property.inquiry_received",
  "governance.issue_raised",
  "governance.gbm_reminder",
  "discussions.mention",
  "discussions.reply",
  "ticket.comment",
  "maintenance.invoice_created",
  "maintenance.payment_success",
  "maintenance.payment_failed",
]);

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  // Acting-as-admin flag — admin surfaces gate on this, not the raw
  // capability, so a role-switched admin viewing the resident shell doesn't
  // see admin-only controls. Mirrors App.js `isAdminView`.
  const { isAdminView } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, m] = await Promise.all([
        apiFetch("/api/users/me/settings", { authRequired: true }),
        apiFetch("/api/users/me", { authRequired: true }),
      ]);
      setSettings(s);
      setMe(m);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !settings) {
    return (
      <div className="vss-shell">
        <div className="vss-loading">Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="vss-shell">
        <div className="vss-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="vss-shell">
      <div className="vss-head">
        <h1>Settings</h1>
        <p>Manage your account, privacy and notifications.</p>
      </div>

      <SectionProfile me={me} onChanged={load} />
      <SectionPassword />
      <SectionPhonePrivacy
        settings={settings}
        onChanged={(next) => setSettings(next)}
      />
      <SectionEventInvites me={me} onChanged={setMe} />
      {settings.emergency_contact_opt_in !== undefined && (
        <SectionEmergencyContact
          settings={settings}
          onChanged={(next) => setSettings(next)}
        />
      )}
      {isAdminView && <SectionCommunitySettings />}
      <SectionNotifications />
      <SectionSessions onRevoked={() => navigate("/")} />
      <SectionDangerZone
        settings={settings}
        onChanged={(next) => setSettings(next)}
      />
    </div>
  );
}

function Section({ icon: Icon, title, description, children }) {
  return (
    <section className="vss-card">
      <div className="vss-card-head">
        <Icon size={16} />
        <h2>{title}</h2>
      </div>
      {description && <p className="vss-card-desc">{description}</p>}
      <div className="vss-card-body">{children}</div>
    </section>
  );
}

function SectionProfile({ me, onChanged }) {
  const navigate = useNavigate();
  return (
    <Section
      icon={UserIcon}
      title="Profile"
      description="Your name, email and alternative phone."
    >
      <div className="vss-row">
        <div className="vss-row-label">Name</div>
        <div className="vss-row-value">{me?.name || "—"}</div>
      </div>
      <div className="vss-row">
        <div className="vss-row-label">Mobile</div>
        <div className="vss-row-value">+91 {me?.phone_number}</div>
      </div>
      <div className="vss-row">
        <div className="vss-row-label">Email</div>
        <div className="vss-row-value">{me?.email || "—"}</div>
      </div>
      <div className="vss-row-actions">
        <button
          className="vss-btn-secondary"
          onClick={() => navigate("/profile")}
        >
          Edit profile
        </button>
      </div>
    </Section>
  );
}

function SectionPassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setMsg("");
    setErr("");
    if (next.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("New password and confirmation don't match.");
      return;
    }
    setBusy(true);
    try {
      const r = await apiFetch("/api/users/me/settings/password", {
        method: "POST",
        authRequired: true,
        body: { current_password: current, new_password: next },
      });
      setMsg(r.message || "Password updated.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      setErr(e.message || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      icon={Lock}
      title="Change password"
      description="At least 8 characters. Changing your password signs you out of every other session."
    >
      <form onSubmit={submit} className="vss-form">
        <label>
          Current password
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <label>
          New password
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </label>
        <label>
          Confirm new password
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </label>
        {err && <div className="vss-msg vss-msg-err">{err}</div>}
        {msg && <div className="vss-msg vss-msg-ok">{msg}</div>}
        <div className="vss-form-actions">
          <button
            type="submit"
            className="vss-btn-primary"
            disabled={busy}
          >
            {busy ? "Saving…" : "Change password"}
          </button>
        </div>
      </form>
    </Section>
  );
}

function SectionEventInvites({ me, onChanged }) {
  const [busy, setBusy] = useState(false);
  const optedOut = !!(me && me.hosted_invite_opt_out);
  const toggle = async () => {
    setBusy(true);
    try {
      const next = !optedOut;
      const r = await apiFetch("/api/users/me", {
        method: "PATCH",
        authRequired: true,
        body: { hosted_invite_opt_out: next },
      });
      onChanged({ ...me, hosted_invite_opt_out: r.hosted_invite_opt_out });
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section
      icon={PartyPopper}
      title="Event invitations"
      description="Neighbours can invite you to events they host. Turn this off if you'd rather not be added to anyone's guest list."
    >
      <label className="vss-toggle">
        <input
          type="checkbox"
          checked={!optedOut}
          onChange={toggle}
          disabled={busy}
        />
        <span>Let other residents invite me to their events</span>
      </label>
    </Section>
  );
}

// Facility-manager-only opt-in: the FM who enables this becomes the "call
// facility manager" contact shown to rejected/pending members on the
// approval screen (and for emergencies). The GET settings payload only
// carries `emergency_contact_opt_in` for FMs, which gates this section.
function SectionEmergencyContact({ settings, onChanged }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      const next = !settings.emergency_contact_opt_in;
      const r = await apiFetch("/api/users/me/settings/emergency-contact", {
        method: "PATCH",
        authRequired: true,
        body: { opt_in: next },
      });
      onChanged({
        ...settings,
        emergency_contact_opt_in: r.emergency_contact_opt_in,
      });
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section
      icon={PhoneCall}
      title="Emergency contact"
      description="When enabled, your name and phone number are shown to members whose villa request is pending or rejected, so they can reach you directly."
    >
      <label className="vss-toggle">
        <input
          type="checkbox"
          checked={!!settings.emergency_contact_opt_in}
          onChange={toggle}
          disabled={busy}
        />
        <span>Be the contact for approval rejections &amp; emergencies</span>
      </label>
    </Section>
  );
}

// Admin-only community-wide feature flags (`/api/community-settings`).
// Reads the current values on mount and PATCHes a single flag on toggle.
// Non-optimistic: the checkbox reflects only confirmed server state, so a
// failed PATCH leaves it on its last good value (mirrors mobile
// `LiveGuardMapRow`). RBAC is also enforced server-side (PATCH is 403 for
// non-admins); this section is merely hidden from non-admins.
function SectionCommunitySettings() {
  const [flags, setFlags] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getCommunitySettings()
      .then((data) => {
        if (alive) setFlags(data);
      })
      .catch((e) => {
        if (alive) setLoadErr(e.message || "Couldn't load community settings.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const toggleLiveGuardMap = async (next) => {
    setBusy(true);
    try {
      const updated = await updateCommunitySettings({
        resident_live_guard_map: next,
      });
      // Adopt the server's confirmed flag map — no optimistic flip.
      setFlags(updated);
    } catch (e) {
      alert(e.message || "Couldn't update the setting");
    } finally {
      setBusy(false);
    }
  };

  const value = !!(flags && flags.resident_live_guard_map);
  // Disable while loading, on a load error, or mid-write. Without the current
  // value the checkbox would render enabled+OFF, which an admin could misread
  // or wrongly flip.
  const disabled = loading || !!loadErr || busy;

  return (
    <Section
      icon={MapPin}
      title="Community settings"
      description="Community-wide controls for everyone in your society. Only admins can change these."
    >
      <label className="vss-toggle">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => toggleLiveGuardMap(e.target.checked)}
          disabled={disabled}
        />
        <span>Show live guard map to residents</span>
      </label>
      <p className="vss-card-foot">
        Residents can open an on-duty guard's live location in their maps app.
        Off by default — when off, residents only see the last verified patrol
        checkpoint.
      </p>
      {loadErr && <div className="vss-msg vss-msg-err">{loadErr}</div>}
    </Section>
  );
}

function SectionPhonePrivacy({ settings, onChanged }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      const next = !settings.phone_public;
      const r = await apiFetch("/api/users/me/settings/phone-public", {
        method: "PATCH",
        authRequired: true,
        body: { phone_public: next },
      });
      onChanged({ ...settings, phone_public: r.phone_public });
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      icon={Phone}
      title="Phone privacy"
      description="Decide whether other members of the society can see your phone number directly, or have to send an OTP request to reveal it."
    >
      <label className="vss-toggle">
        <input
          type="checkbox"
          checked={!!settings.phone_public}
          onChange={toggle}
          disabled={busy}
        />
        <span>
          Show my phone number to logged-in members of the society
        </span>
      </label>
      <p className="vss-card-foot">
        Property listings have their own per-listing override. This is
        the default for the directory.
      </p>
    </Section>
  );
}

function SectionNotifications() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({});

  useEffect(() => {
    apiFetch("/api/notification-prefs", { authRequired: true })
      .then((data) => {
        // Filter to the curated web subset, preserving backend order.
        setPrefs(data.filter((p) => WEB_NOTIF_CATEGORIES.has(p.category)));
      })
      .catch(() => setPrefs([]))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (category, enabled) => {
    // Optimistic update.
    setPrefs((cur) =>
      cur ? cur.map((p) => (p.category === category ? { ...p, enabled } : p)) : cur
    );
    setPending((s) => ({ ...s, [category]: true }));
    try {
      const updated = await apiFetch(
        `/api/notification-prefs/${encodeURIComponent(category)}`,
        { method: "PATCH", authRequired: true, body: { enabled } }
      );
      setPrefs((cur) =>
        cur ? cur.map((p) => (p.category === category ? updated : p)) : cur
      );
    } catch (e) {
      // Roll back optimistic update on error.
      setPrefs((cur) =>
        cur ? cur.map((p) => (p.category === category ? { ...p, enabled: !enabled } : p)) : cur
      );
      alert(e.message);
    } finally {
      setPending((s) => ({ ...s, [category]: false }));
    }
  };

  return (
    <Section
      icon={Bell}
      title="Notification preferences"
      description="Control which in-app events send you a push notification. All types are enabled by default (opt-out)."
    >
      {loading ? (
        <p className="vss-card-foot">Loading…</p>
      ) : (
        <div className="vss-notif-list">
          {(prefs || []).map((p) => (
            <label key={p.category} className="vss-toggle">
              <input
                type="checkbox"
                checked={!!p.enabled}
                onChange={(e) => toggle(p.category, e.target.checked)}
                disabled={!!pending[p.category]}
              />
              <span>{p.label}</span>
            </label>
          ))}
        </div>
      )}
    </Section>
  );
}

function SectionSessions({ onRevoked }) {
  const [busy, setBusy] = useState(false);
  const revoke = async () => {
    if (
      !window.confirm(
        "Sign out of every session, including this one? You'll need to log in again."
      )
    )
      return;
    setBusy(true);
    try {
      await apiFetch("/api/users/me/settings/revoke-sessions", {
        method: "POST",
        authRequired: true,
        body: {},
      });
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      onRevoked && onRevoked();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section
      icon={KeyRound}
      title="Active sessions"
      description="Sign out of every browser and device this account is logged into — including this one. You'll need to log in again afterwards."
    >
      <div className="vss-form-actions">
        <button
          className="vss-btn-secondary"
          onClick={revoke}
          disabled={busy}
        >
          {busy ? "Signing out…" : "Sign out of all sessions"}
        </button>
      </div>
    </Section>
  );
}

function SectionDangerZone({ settings, onChanged }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const request = async () => {
    if (
      !window.confirm(
        "Send a deletion request to the admins? Your data stays until they confirm."
      )
    )
      return;
    setBusy(true);
    setErr("");
    try {
      const r = await apiFetch("/api/users/me/settings/delete-request", {
        method: "POST",
        authRequired: true,
        body: { reason: reason.trim() || null },
      });
      onChanged({
        ...settings,
        delete_requested: true,
        delete_requested_at: r.requested_at,
      });
      setReason("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await apiFetch("/api/users/me/settings/delete-request", {
        method: "DELETE",
        authRequired: true,
      });
      onChanged({ ...settings, delete_requested: false, delete_requested_at: null });
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="vss-card vss-card-danger">
      <div className="vss-card-head">
        <Trash2 size={16} />
        <h2>Delete account</h2>
      </div>
      <div className="vss-card-danger-warn">
        <strong>This is permanent once approved.</strong> An admin reviews
        every request — your data stays put until they confirm. You'll
        keep app access in the meantime.
      </div>
      <div className="vss-card-body">
      {settings.delete_requested ? (
        <div className="vss-msg vss-msg-warn">
          A deletion request is pending review. We'll email you when an
          admin closes it out.
          <button
            type="button"
            className="vss-link"
            onClick={cancel}
            disabled={busy}
          >
            Cancel my request
          </button>
        </div>
      ) : (
        <>
          <label>
            Reason (optional)
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={1000}
              placeholder="Anything you'd like the admins to know."
            />
          </label>
          {err && <div className="vss-msg vss-msg-err">{err}</div>}
          <div className="vss-form-actions">
            <button
              className="vss-btn-danger"
              onClick={request}
              disabled={busy}
            >
              Request account deletion
            </button>
          </div>
        </>
      )}
      </div>
    </section>
  );
}
